import React, { useEffect, useState } from "react";
import { Listbox, Transition } from '@headlessui/react';
import { MdCheck } from "react-icons/md";
import { HiChevronUpDown } from "react-icons/hi2";
import { Fragment } from "react";
import {
  getAllCompanies,
  createCompanySecure,
  suspendCompanySecure,
  deleteCompanySecure,
  editCompanyDetails,
} from "../services/superAdminService";
import { PiSpinnerGapLight } from "react-icons/pi";
import BoxWrapper from "./BoxWrapper";
import Loader from "../components/shared/Loader";
import { useForm } from "react-hook-form";
import DialogModal from "../reusables/DialogModal";
import { Toaster, toast } from 'react-hot-toast';
import CompanyTable from "./superAdmin/CompanyTable";
import DeleteCompanyDialog from "./superAdmin/DeleteCompanyDialog";
import SuspendCompanyDialog from "./superAdmin/SuspendCompanyDialog";
import EditCompanyDialog from "./superAdmin/EditCompanyDialog";
import UserLogsViewer from "./superAdmin/UserLogsViewer";

const SuperAdminDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [companyToSuspend, setCompanyToSuspend] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    const data = await getAllCompanies();
    setCompanies(data);
    setLoadingCompanies(false);
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const plans = ["Basic", "Standard", "Premium"];
  const [selectedPlan, setSelectedPlan] = useState("");
  useEffect(() => {
    setValue("plan", selectedPlan);
  }, [selectedPlan, setValue]);

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    setValue: setValueEdit,
    reset: resetEdit,
    formState: { errors: errorsEdit },
    watch: watchEdit,
  } = useForm(); // for edit form

  useEffect(() => {
    fetchCompanies();
  }, []);

  const onSubmit = async (data) => {
    await handleCreate(data);
    reset();
  };

  const generateCompanyId = () => {
    return `comp_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
  };

  const handleCreate = async (data) => {
    const { companyName, adminEmail, adminPassword, maxUsers, plan } = data;

    const companyId = generateCompanyId();
    setLoading(true);
    try {
      await createCompanySecure(
        companyId,
        companyName,
        adminEmail,
        adminPassword,
        parseInt(maxUsers),
        plan
      );

      fetchCompanies();
    } catch (err) {
      toast.error("Failed to create company: " + err.message);
    } finally {
      setLoading(false);
      toast.success("Company created successfully!");
    }
  };

  const handleSuspend = async (companyId, isSuspended) => {
    setLoading(true);
    try {
      await suspendCompanySecure(companyId, !isSuspended);
      fetchCompanies();
    } catch (err) {
      toast.error("Failed to suspend company: " + err.message);
    } finally {
      setLoading(false);
      toast.success(`Company ${isSuspended ? 'activated' : 'suspended'} successfully!`);
    }
  };

  const handleDelete = async (companyId) => {
    setLoading(true);
    try {
      await deleteCompanySecure(companyId);
      fetchCompanies();
    } catch (err) {
      toast.error("Failed to delete company: " + err.message);
    } finally {
      setLoading(false);
      toast.success("Company deleted successfully!");
    }
  };

  const handleEditCompany = (company) => {
    setCompanyToEdit(company);
    setValueEdit("editName", company.name);
    setValueEdit("editMaxUsers", company.maxUsers ?? "");
    setValueEdit("editAdminEmail", "");
    setValueEdit("editAdminPassword", "");
    setValueEdit("editPlan", company.plan || "");
    setEditDialogOpen(true);
  };

  return (
    <div className="w-full p-6">
      <Toaster position="top-center" />
      <DeleteCompanyDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          if (companyToDelete) {
            setDeleteDialogOpen(false);
            await handleDelete(companyToDelete);
            setCompanyToDelete(null);
          }
        }}
      />
      <SuspendCompanyDialog
        isOpen={suspendDialogOpen}
        isSuspended={companyToSuspend?.isSuspended}
        onClose={() => setSuspendDialogOpen(false)}
        onConfirm={async () => {
          if (companyToSuspend) {
            setSuspendDialogOpen(false);
            await handleSuspend(companyToSuspend.id, companyToSuspend.isSuspended);
            setCompanyToSuspend(null);
          }
        }}
      />
      <EditCompanyDialog
        isOpen={editDialogOpen}
        company={companyToEdit}
        onClose={() => {
          setEditDialogOpen(false);
          resetEdit();
        }}
        onSubmit={handleSubmitEdit(async (values) => {
          if (!companyToEdit) return;

          const updates = {
            companyId: companyToEdit.id,
            ...(values.editName && { newName: values.editName }),
            ...(values.editMaxUsers && { newMaxUsers: parseInt(values.editMaxUsers) }),
            ...(values.editAdminEmail && { newAdminEmail: values.editAdminEmail }),
            ...(values.editAdminPassword && { newAdminPassword: values.editAdminPassword }),
            ...(values.editPlan && { newPlan: values.editPlan }),
          };

          setEditDialogOpen(false);
          setLoading(true);
          try {
            await editCompanyDetails(updates);
            fetchCompanies();
          } catch (err) {
            alert("Update failed: " + err.message);
          } finally {
            setLoading(false);
            setCompanyToEdit(null);
          }
        })}
        register={registerEdit}
        errors={errorsEdit}
        setValue={setValueEdit}
        watch={watchEdit}
      />
      {loading && <Loader />}
      <div className="">
        <BoxWrapper title={'Clients'}>
          <CompanyTable
            companies={companies}
            loading={loadingCompanies}
            onSuspend={(id, isSuspended) => {
              setCompanyToSuspend({ id, isSuspended });
              setSuspendDialogOpen(true);
            }}
            onEdit={handleEditCompany}
            onDelete={(companyId) => {
              setCompanyToDelete(companyId);
              setDeleteDialogOpen(true);
            }}
          />
        </BoxWrapper>
      </div>

      <div className="flex gap-6 py-6">
        <div className="w-2/4 ">
          <BoxWrapper title={'Create Company Account'} defaultExpanded={false}>
            <div className='flex-1 '>
              <div className="py-4">

                <div className="mt-2 grid gap-3">

                  <form onSubmit={handleSubmit(onSubmit)} className="mt-2 grid gap-3">
                    <input
                      placeholder="Company Name"
                      {...register("companyName", { required: "Company name is required" })}
                      className="p-3 rounded-md text-gray-500 focus:outline-cyan-500 border-2 placeholder:text-gray-400 placeholder:font-light placeholder:text-sm"
                    />
                    {errors.companyName && (
                      <span className="text-red-500 text-sm">{errors.companyName.message}</span>
                    )}

                    <input
                      placeholder="Admin Email"
                      type="email"
                      {...register("adminEmail", { required: "Admin email is required" })}
                      className="p-3 rounded-md text-gray-500 focus:outline-cyan-500 border-2 placeholder:text-gray-400 placeholder:font-light placeholder:text-sm"
                    />
                    {errors.adminEmail && (
                      <span className="text-red-500 text-sm">{errors.adminEmail.message}</span>
                    )}

                    <input
                      placeholder="Admin Password"
                      type="password"
                      {...register("adminPassword", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                      className="p-3 rounded-md text-gray-500 focus:outline-cyan-500 border-2 placeholder:text-gray-400 placeholder:font-light placeholder:text-sm"
                    />
                    {errors.adminPassword && (
                      <span className="text-red-500 text-sm">{errors.adminPassword.message}</span>
                    )}

                    <input
                      placeholder="Max Users"
                      type="number"
                      {...register("maxUsers", { required: "Max users is required", min: 1 })}
                      className="p-3 rounded-md text-gray-500 focus:outline-cyan-500 border-2 placeholder:text-gray-400 placeholder:font-light placeholder:text-sm"
                    />
                    {errors.maxUsers && (
                      <span className="text-red-500 text-sm">{errors.maxUsers.message}</span>
                    )}

                    <Listbox value={selectedPlan} onChange={setSelectedPlan}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left border-2 text-gray-500 shadow-sm focus:outline-cyan-500">
                          <span className="block truncate">{selectedPlan || "Choose an option"}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <HiChevronUpDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-50">
                            {plans.map((plan, idx) => (
                              <Listbox.Option
                                key={idx}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                                  }`
                                }
                                value={plan}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {plan}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                        <MdCheck className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                    <input type="hidden" {...register("plan", { required: "Plan selection is required" })} />
                    {errors.plan && (
                      <span className="text-red-500 text-sm">{errors.plan.message}</span>
                    )}

                    <button
                      type="submit"
                      className="bg-customeBlack text-white py-3 rounded flex items-center justify-center mt-4"
                      disabled={loading}
                    >
                      {loading && (
                        <PiSpinnerGapLight className="animate-spin h-4 w-4 text-white" />
                      )}
                      <span>{loading ? "Creating..." : "Create"}</span>
                    </button>
                  </form>

                </div>


              </div>
            </div>
          </BoxWrapper>
        </div>
      </div>

      <div className="w-full ">
        <BoxWrapper title={'User Logs'}>
          <UserLogsViewer />
        </BoxWrapper>
      </div>



    </div>
  );
};

export default SuperAdminDashboard;